import React from "react";

import { Grid } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  CheckBoxBase,
  Input,
  QuickOptionsWithSelect,
  TextArea,
} from "components/Form/FormikControls";
import SlimButton, { SlimSecondaryButton } from "components/Button/SlimButton";
import { Row } from "components/Layout";

import {
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getMLAValidatedSectionsBySaleSubTypeId,
  getSaleSubTypeById,
  getSpeciesAttributes,
} from "selectors";
import {
  HasDeploymentMarks,
  OtherMarkingField,
  ProductSelectField,
  SaleLotLabelSelectorField,
} from "components/Form/Fields";
import { ForCattle, ForSheep } from "containers/ForSpecies";
import { DentitionField } from "components/Dentition/Index";
import { ForHooks, ForNotHooks } from "containers/ForSaleType";
import {
  useHasMLAOrDataPermission,
  useHasSaleyardOrDeploymentPermission,
  useToggle,
} from "hooks";
import MarkingField from "components/Form/Fields/MarkingField";
import { AnimalHealthField } from "components/Form/Fields/AnimalHealthField";
import { EIDExemptionReasonField } from "components/Form/Fields/EIDExemptionField";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { SaleType } from "constants/sale";
import { withNamespace } from "components/Form/FormikControls/lib";
import { FormCollapse } from "components/Form";
import { EstAvgWeightRangeField } from "components/WeightRange";
import { DressingRangeField } from "components/DressingRange";
import {
  FrameSectionSimple,
  PregStatusJoiningSection,
  SheepWeightSectionSimple,
} from "components/DetailedSaleLotModal/AdvancedDrafting/Sheep";
import { CattleWeightSectionSimple } from "components/DetailedSaleLotModal/AdvancedDrafting/Cattle";
import { SubtleBadge } from "components/Badge";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";

import {
  TransactionalSaleFormValues,
  defaultSkinLot,
} from "components/MobileTransactionalModal/lib/types";
import { FormRow } from "components/MobileTransactionalModal/Tabs/FormRow";

const SkinsFormRow = styled(Row).attrs({
  fullWidth: true,
  flex: true,
  alignCenter: true,
  justifyBetween: true,
  marginVertical: 2,
})``;

interface WhatTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleLotNamespace: string;
  saleNamespace: string;
  consignmentNamespace: string;
  skinLotsNamespace: string;
  saleType?: SaleType | null;
  saleSubTypeId?: number | null;
}

export const WhatTab = ({
  deploymentId,
  speciesId,
  saleLotNamespace,
  saleNamespace,
  consignmentNamespace,
  skinLotsNamespace,
  saleType: saleTypeProp,
  saleSubTypeId: saleSubTypeIdProp,
}: WhatTabProps) => {
  const { values, setFieldValue } =
    useFormikContext<TransactionalSaleFormValues>();

  const saleType = values.sale?.sale_type ?? saleTypeProp ?? null;
  const saleSubTypeId =
    values.sale?.sale_sub_type_id ?? saleSubTypeIdProp ?? null;

  const skinsLotsHaveQuantity =
    values.skinLots?.some(skinLot => !!skinLot.quantity) ?? false;

  const [showMoreSaleLotFields, toggleShowMoreSaleLotFields] = useToggle(false);
  const [includeSkins, toggleIncludeSkins] = useToggle(skinsLotsHaveQuantity);

  const handleToggleSkins = () => {
    if (includeSkins) {
      setFieldValue(skinLotsNamespace, [defaultSkinLot]);
      setFieldValue(
        withNamespace(consignmentNamespace, "billing_reference"),
        "",
      );
    }
    toggleIncludeSkins();
  };

  const {
    saleLot: {
      age_id: ageId,
      product_id: productId,
      sex_id: sexId,
      breed_id: breedId,
    },
  } = values;

  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );
  const { sexes } = useSelector(getSpeciesAttributes);

  const hasWeightRangePerm = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureWeightRange,
    SaleyardPermissions.featureWeightRange,
  );

  const dentitionOptions = useSelector(getDentitionOptionsByAgeId(ageId));

  const {
    mlaValidateWeightSection,
    mlaValidateJoiningSection,
    mlaValidateFrameSection,
    mlaValidateAge,
  } = useSelector(getMLAValidatedSectionsBySaleSubTypeId(saleSubTypeId));

  const saleSubType = useSelector(getSaleSubTypeById(saleSubTypeId)) || {};
  const areStudFeaturesEnabled = saleSubType.enableStudFeatures;
  const isDentitionEnabled = saleSubType.enableDentition;
  const isWeightRangeAndDressingEnabled =
    saleSubType.enableWeightRangeAndDressing;

  const hasMLAOrDataPermission = useHasMLAOrDataPermission();

  const showWeightAndDressing =
    mlaValidateWeightSection &&
    isWeightRangeAndDressingEnabled &&
    hasWeightRangePerm;

  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);

  return (
    <>
      <Grid container item xs={12}>
        <FormRow>
          <Grid item xs={6}>
            <Input
              name={withNamespace(saleLotNamespace, "quantity")}
              label="Head"
              type="number"
              inputMode="numeric"
              required
            />
          </Grid>
          <ForSheep selectedSpeciesId={speciesId}>
            <ForHooks selectedSaleType={saleType}>
              <Grid item xs={5}>
                <CheckBoxBase
                  label="Include Skins"
                  onChange={handleToggleSkins}
                  value={includeSkins}
                />
              </Grid>
            </ForHooks>
          </ForSheep>
        </FormRow>

        <FormRow>
          <Grid item xs={12}>
            <ProductSelectField
              ns={saleLotNamespace}
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
              selectedSaleType={saleType}
            />
          </Grid>
        </FormRow>

        {mlaValidateAge && productId && !ageId && (
          <ForSheep selectedSpeciesId={speciesId}>
            <Grid item xs={12}>
              <QuickOptionsWithSelect
                ns={saleLotNamespace}
                name="age_id"
                label="Age"
                options={ageOptions}
                missingMLAFieldsWarningEnabled
              />
            </Grid>
          </ForSheep>
        )}

        <ForCattle selectedSpeciesId={speciesId}>
          <QuickOptionsWithSelect
            ns={saleLotNamespace}
            name="age_id"
            label="Age"
            options={ageOptions}
            missingMLAFieldsWarningEnabled
          />
        </ForCattle>

        {selectedSexHasProgeny && (
          <FormRow>
            <Grid item xs={12}>
              <Input
                type="number"
                label="Progeny Count"
                name={withNamespace(saleLotNamespace, "quantity_progeny")}
              />
            </Grid>
          </FormRow>
        )}

        {isDentitionEnabled && dentitionOptions.length > 0 && (
          <FormRow>
            <Grid item xs={12}>
              <DentitionField
                name={withNamespace(saleLotNamespace, "dentitionId")}
              />
            </Grid>
          </FormRow>
        )}

        <FormRow>
          <Grid item xs={12}>
            <QuickOptionsWithSelect
              ns={saleLotNamespace}
              name="breed_id"
              label="Breed"
              options={breedOptions}
              missingMLAFieldsWarningEnabled
            />
          </Grid>
        </FormRow>

        {includeSkins && (
          <>
            <FieldArray name={skinLotsNamespace}>
              {({ push, remove }) => (
                <>
                  {(values.skinLots || []).map((_, index) => (
                    <SkinsFormRow key={index}>
                      <Grid item xs={4}>
                        <Input
                          name={`${skinLotsNamespace}[${index}].quantity`}
                          label={index === 0 ? "Skin Qty" : undefined}
                          type="number"
                          inputMode="numeric"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Input
                          name={`${skinLotsNamespace}[${index}].unitPrice`}
                          label={index === 0 ? "Skin Price" : undefined}
                          beforeSymbol="$"
                          type="number"
                          decimal
                        />
                      </Grid>
                      <Grid
                        item
                        xs={2}
                        container
                        alignItems="flex-end"
                        style={index === 0 ? { marginBottom: "-18px" } : {}}
                      >
                        <SlimSecondaryButton
                          type="button"
                          fullWidth
                          disabled={index === 0 && values.skinLots.length === 1}
                          onClick={() => remove(index)}
                        >
                          <FaIcon icon={faTrash} />
                        </SlimSecondaryButton>
                      </Grid>
                    </SkinsFormRow>
                  ))}
                  <SkinsFormRow>
                    <Grid item xs={12}>
                      <Row justifyEnd>
                        <SlimButton
                          type="button"
                          onClick={() => push(defaultSkinLot)}
                        >
                          Add line
                        </SlimButton>
                      </Row>
                    </Grid>
                  </SkinsFormRow>
                </>
              )}
            </FieldArray>
            <FormRow>
              <Input
                label="External References/Kill Id"
                name={withNamespace(consignmentNamespace, "billing_reference")}
                maxLength={255}
                tooltip="This value will appear in the header of related Invoices/RCTI's and line items on the Invoice may be grouped by this value."
              />
            </FormRow>
          </>
        )}
      </Grid>

      <FormCollapse
        isOpen={showMoreSaleLotFields}
        onToggle={toggleShowMoreSaleLotFields}
        header={<SubtleBadge>More</SubtleBadge>}
      >
        <>
          {areStudFeaturesEnabled && (
            <>
              <FormRow>
                <Grid item xs={12}>
                  <Input
                    label="Tag #"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.tagNumber",
                    )}
                  />
                </Grid>
              </FormRow>
              <FormRow>
                <Grid item xs={12} data-tour="animalName">
                  <TextArea
                    label="Animal Name"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.animalName",
                    )}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {showWeightAndDressing && (
            <>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <EstAvgWeightRangeField
                    ns={saleLotNamespace}
                    name="estimatedAverageWeightId"
                    saleRoundIdField={withNamespace(
                      saleNamespace,
                      "sale_round_id",
                    )}
                    disabled={false}
                    selectedSaleSubType={saleSubType}
                  />
                </Grid>
              </FormRow>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <DressingRangeField
                    ns={saleLotNamespace}
                    name="dressingRangeId"
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {mlaValidateWeightSection && hasMLAOrDataPermission && (
            <>
              <ForSheep selectedSpeciesId={speciesId}>
                <FormRow>
                  <SheepWeightSectionSimple
                    namespace={saleLotNamespace}
                    readOnly={false}
                  />
                </FormRow>
              </ForSheep>
              <ForCattle selectedSpeciesId={speciesId}>
                <FormRow>
                  <CattleWeightSectionSimple
                    namespace={saleLotNamespace}
                    readOnly={false}
                  />
                </FormRow>
              </ForCattle>
            </>
          )}
          {mlaValidateJoiningSection && hasMLAOrDataPermission && (
            <FormRow>
              <PregStatusJoiningSection
                namespace={saleLotNamespace}
                readOnly={false}
              />
            </FormRow>
          )}
          {mlaValidateFrameSection && (
            <FrameSectionSimple namespace={saleLotNamespace} readOnly={false} />
          )}
          <ForNotHooks selectedSaleType={saleType}>
            <HasDeploymentMarks
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
            >
              <FormRow>
                <Grid item xs={12}>
                  <MarkingField
                    deploymentId={deploymentId}
                    name={withNamespace(saleLotNamespace, "marks")}
                    selectedSpeciesId={speciesId}
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </HasDeploymentMarks>
            <FormRow>
              <Grid item xs={12}>
                <OtherMarkingField
                  deploymentId={deploymentId}
                  name={withNamespace(saleLotNamespace, "marks")}
                  disabled={false}
                  selectedSpeciesId={speciesId}
                />
              </Grid>
            </FormRow>
          </ForNotHooks>
          <FormRow>
            <Grid item xs={12}>
              <SaleLotLabelSelectorField
                label="Labels"
                name={withNamespace(saleLotNamespace, "labels")}
                deploymentId={deploymentId}
                selectedSpeciesId={speciesId}
              />
            </Grid>
          </FormRow>
          <FormRow>
            <Grid data-tour="category" item xs={12}>
              <AnimalHealthField
                ns={saleLotNamespace}
                disabled={false}
                isClearable
                selectedBreedId={breedId}
              />
            </Grid>
          </FormRow>
          <ForNotHooks selectedSaleType={saleType}>
            <ForSheep selectedSpeciesId={speciesId}>
              <FormRow>
                <Grid data-tour="eid" item xs={12}>
                  <EIDExemptionReasonField
                    ns={saleLotNamespace}
                    isClearable
                    disabled={false}
                    selectedSpeciesId={speciesId}
                  />
                </Grid>
              </FormRow>
            </ForSheep>
          </ForNotHooks>
          <FormRow>
            <Grid item xs={12} data-tour="notes">
              <TextArea
                label="Sale Lot Notes"
                name={withNamespace(saleLotNamespace, "note")}
                disabled={false}
                rows={4}
              />
            </Grid>
          </FormRow>
        </>
      </FormCollapse>
    </>
  );
};
